import { useState } from "react";
import { TrashIcon, ExternalLinkIcon, CloseIcon, EditIcon, CheckSmallIcon } from "./Icons";
import "./ExternalCalendarsPanel.css";

function CalendarItem({ cal, onDelete, onSave }) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(cal.name);
  const [editUrl, setEditUrl]   = useState(cal.url);
  const [editError, setEditError] = useState("");

  const handleSave = () => {
    const trimmedName = editName.trim();
    const trimmedUrl  = editUrl.trim();
    if (!trimmedName) { setEditError("Name required"); return; }
    if (!trimmedUrl)  { setEditError("URL required"); return; }
    try { new URL(trimmedUrl); } catch {
      setEditError("Invalid URL");
      return;
    }
    onSave(cal.id, { name: trimmedName, url: trimmedUrl });
    setEditing(false);
    setEditError("");
  };

  const handleCancel = () => {
    setEditName(cal.name);
    setEditUrl(cal.url);
    setEditError("");
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="ext-cal-item ext-cal-item-editing">
        <div className="ext-cal-edit-fields">
          {editError && <div className="ext-cal-error">{editError}</div>}
          <input
            className="ext-cal-input"
            value={editName}
            onChange={(e) => { setEditName(e.target.value); setEditError(""); }}
            placeholder="Name"
            autoFocus
          />
          <input
            className="ext-cal-input"
            value={editUrl}
            onChange={(e) => { setEditUrl(e.target.value); setEditError(""); }}
            placeholder="URL (https://...)"
          />
        </div>
        <div className="ext-cal-edit-actions">
          <button className="btn-icon ext-cal-save" onClick={handleSave} title="Save">
            <CheckSmallIcon />
          </button>
          <button className="btn-icon" onClick={handleCancel} title="Cancel">
            <CloseIcon />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ext-cal-item">
      <a
        href={cal.url}
        target="_blank"
        rel="noopener noreferrer"
        className="ext-cal-link"
        title={cal.url}
      >
        <span className="ext-cal-name">{cal.name}</span>
        <span className="ext-cal-link-icon"><ExternalLinkIcon /></span>
      </a>
      <button
        className="btn-icon ext-cal-action"
        onClick={() => setEditing(true)}
        title="Edit calendar"
      >
        <EditIcon />
      </button>
      <button
        className="btn-icon delete ext-cal-action"
        onClick={() => onDelete(cal.id)}
        title="Delete calendar"
      >
        <TrashIcon />
      </button>
    </div>
  );
}

export default function ExternalCalendarsPanel({ open, onClose, calendars, onAdd, onDelete, onSave }) {
  const [name, setName] = useState("");
  const [url, setUrl]   = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedUrl  = url.trim();
    if (!trimmedName) { setError("Name is required"); return; }
    if (!trimmedUrl)  { setError("URL is required"); return; }
    try { new URL(trimmedUrl); } catch {
      setError("Enter a valid URL (include https://)");
      return;
    }
    onAdd({ name: trimmedName, url: trimmedUrl });
    setName("");
    setUrl("");
    setError("");
  };

  return (
    <>
      {open && <div className="ext-cal-backdrop" onClick={onClose} />}
      <div className={"ext-cal-panel" + (open ? " open" : "")}>
        <div className="ext-cal-header">
          <span className="ext-cal-title">External Calendars</span>
          <button className="ext-cal-close btn-icon" onClick={onClose} title="Close">
            <CloseIcon />
          </button>
        </div>

        <div className="ext-cal-list">
          {calendars.length === 0 && (
            <div className="ext-cal-empty">No calendars added yet</div>
          )}
          {calendars.map((cal) => (
            <CalendarItem key={cal.id} cal={cal} onDelete={onDelete} onSave={onSave} />
          ))}
        </div>

        <form className="ext-cal-form" onSubmit={handleSubmit}>
          <div className="ext-cal-form-title">Add Calendar</div>
          {error && <div className="ext-cal-error">{error}</div>}
          <input
            className="ext-cal-input"
            placeholder="Name (e.g. Google Calendar)"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(""); }}
          />
          <input
            className="ext-cal-input"
            placeholder="URL (https://...)"
            value={url}
            onChange={(e) => { setUrl(e.target.value); setError(""); }}
          />
          <button type="submit" className="ext-cal-submit">+ Add</button>
        </form>
      </div>
    </>
  );
}
